/**
 * Market Briefing Quotes
 *
 * Curated quotes for each briefing period (Morning, Midday, Evening)
 * Each set contains finance-themed quotes appropriate for the time of day
 */

import { BriefingPeriod } from './market-briefing-types';

// Morning Quotes - Dawn of trading, fresh starts, optimism
const MORNING_QUOTES = [
  "The early bird catches the best trades.",
  "Markets wake up before coffee kicks in.",
  "Fortune favors the prepared investor.",
  "Every market day is a new opportunity.",
  "The opening bell rings with possibility.",
  "Start strong, trade smart, finish wealthy.",
  "Morning momentum sets the day's tone.",
  "Wake up, the markets are already moving.",
  "The best trades happen before lunch.",
  "Discipline in the morning, profits by evening."
];

// Midday Quotes - Peak activity, momentum, market madness
const MIDDAY_QUOTES = [
  "Midday madness separates winners from watchers.",
  "The market never sleeps, neither should your strategy.",
  "Peak hours demand peak performance.",
  "Volume speaks louder than words.",
  "Trends are made in the middle of the day.",
  "When volatility spikes, opportunity knocks.",
  "The real action happens at high noon.",
  "Stay sharp when the market gets choppy.",
  "Momentum is everything in the midday rush.",
  "This is where positions are won or lost."
];

// Evening Quotes - Reflection, closing, wisdom, patience
const EVENING_QUOTES = [
  "The closing bell reveals the day's truth.",
  "Patience pays dividends after hours.",
  "Evening is for reflection, not regrets.",
  "Markets close, but strategies never sleep.",
  "Today's close is tomorrow's opportunity.",
  "Wisdom comes after the dust settles.",
  "The best investors know when to rest.",
  "After-hours insights for tomorrow's gains.",
  "Sunset on the markets, sunrise on analysis.",
  "Close the day strong, plan for tomorrow."
];

/**
 * Get a random quote for the specified briefing period
 * @param period - The briefing period (morning, midday, evening)
 * @returns A random quote string for that period
 */
export function getRandomQuote(period: BriefingPeriod): string {
  let quotes: string[];

  switch (period) {
    case 'morning':
      quotes = MORNING_QUOTES;
      break;
    case 'midday':
      quotes = MIDDAY_QUOTES;
      break;
    case 'evening':
      quotes = EVENING_QUOTES;
      break;
    default:
      quotes = MORNING_QUOTES;
  }

  const randomIndex = Math.floor(Math.random() * quotes.length);
  return quotes[randomIndex];
}

/**
 * Get all quotes for a specific period
 * @param period - The briefing period
 * @returns Array of all quotes for that period
 */
export function getAllQuotes(period: BriefingPeriod): string[] {
  switch (period) {
    case 'morning':
      return [...MORNING_QUOTES];
    case 'midday':
      return [...MIDDAY_QUOTES];
    case 'evening':
      return [...EVENING_QUOTES];
    default:
      return [...MORNING_QUOTES];
  }
}

/**
 * Get a deterministic quote based on date (same quote for the same day)
 * @param period - The briefing period
 * @param date - Optional date (defaults to today)
 * @returns A quote that will be consistent for the given date
 */
export function getDailyQuote(period: BriefingPeriod, date: Date = new Date()): string {
  const quotes = getAllQuotes(period);

  // Create a seed from the date (YYYYMMDD format)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateSeed = parseInt(`${year}${month}${day}`, 10);

  // Use the seed to select a consistent quote for the day
  const index = dateSeed % quotes.length;
  return quotes[index];
}
